import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { redirect } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import type { Args } from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class BillingPopupAgencyBlockerComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.analyticsService.event(TrackingEvent.AGENCY_LIMITATION_HIT_POPUP_SHOWN);
	}

	get limitsBreached() {
		return this.billingService.agencyClientPackDowngradeLimitsBreached!;
	}

	get isClient() {
		return this.accountService.isClient;
	}

	get isAdmin() {
		return this.currentUserService.isAdmin;
	}

	get actionText() {
		return this.isClient ? 'Go to organization' : this.isAdmin ? 'Review usage' : '';
	}

	get title() {
		if (!this.isClient && this.isAdmin) {
			return 'Reduce your usage or upgrade to continue';
		} else {
			return 'Some features are temporarily unavailable';
		}
	}

	get text() {
		const intro =
			'Your organization has exceeded the limits included in the current plan, so some features are unavailable.';

		const limitsText = Object.values(this.limitsBreached)
			.map((f) => f.name)
			.join(' and ');

		if (this.isClient) {
			// Agency user viewing a client space – they must switch back to the Agency account to act
			return `${intro} Return to your organization and reduce the number of ${limitsText}, or purchase additional account packs to restore access.`;
		} else if (this.isAdmin) {
			// Agency admin in the Agency account – they can open the downgrade flow to reduce usage
			return `${intro} Reduce the number of ${limitsText}, or purchase additional account packs to restore access.`;
		} else {
			// Agency non-admin – they cannot act, so point them to the Account owner / Administrator
			return `${intro} Contact an organization admin to reduce usage or purchase additional account packs.`;
		}
	}

	get showPurchasePacks() {
		return !this.isClient && this.isAdmin;
	}

	@action
	doPrimaryAction() {
		if (this.isClient) {
			const space = this.mmSpacesService.agencySpaceModel;
			if (space) {
				redirect(space.loginUrl);
			}
		} else {
			this.billingService.openAgencyDowngradePopup();
		}
	}

	@action
	purchasePacks() {
		this.billingService.openClientPacksPurchasePopup();
		this.close();
	}

	<template>
		<div class="content">
			<CommonUiIconComponent @name="Clients" @size="2xl" />
			<h3>
				{{this.title}}
			</h3>
			<p>
				{{this.text}}
			</p>
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					{{#if this.showPurchasePacks}}
						<SubmitButtonComponent
							@text="Re-purchase packs"
							@theme="blue-outlined"
							@extraClass="save"
							@onTrigger={{this.purchasePacks}}
						/>
					{{/if}}
					{{#if this.actionText}}
						<SubmitButtonComponent
							@text={{this.actionText}}
							@extraClass="save"
							@onTrigger={{this.doPrimaryAction}}
						/>
					{{/if}}
				</div>
			</div>
		</div>
	</template>
}
